import { motion } from 'framer-motion';
import { 
  Trophy, 
  Medal,
  Crown,
  Star,
  Clock
} from 'lucide-react';
import { formatINR } from '@/utils/currency';
import BrokerSidebar from '@/components/broker/BrokerSidebar';
import { BROKER_TIERS } from '@/config/brokerTiers';

const badges = Object.fromEntries(
  BROKER_TIERS.map(t => [t.key, { label: t.name, threshold: t.threshold, color: `${t.color} ${t.bg} ${t.border}` }])
);

export default function BrokerLeaderboard() {
  return (
    <div className="min-h-screen bg-[#030303] flex">
      <BrokerSidebar />

      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-light tracking-tight text-white mb-1">
            Leaderboard
          </h1>
          <p className="text-white/50 text-sm">Top performing brokers by capital raised</p>
        </div>

        {/* Coming Soon Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto mt-12"
        >
          <div className="bg-gradient-to-b from-yellow-400/5 to-transparent border border-yellow-400/20 rounded-sm p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-yellow-400/10 rounded-full flex items-center justify-center">
              <Clock className="w-10 h-10 text-yellow-400" />
            </div>
            
            <h2 className="text-2xl font-light text-white mb-3">Coming Soon</h2>
            <p className="text-white/50 mb-8 max-w-md mx-auto">
              We're building an exciting leaderboard experience to showcase top-performing brokers. 
              Stay tuned for rankings, badges, and rewards!
            </p>

            {/* Preview of Badge Tiers */}
            <div className="border-t border-white/10 pt-8 mt-8">
              <p className="text-xs text-white/40 uppercase tracking-wider mb-4">Badge Tiers Preview</p>
              <div className="grid grid-cols-5 gap-3">
                {Object.entries(badges).map(([key, badge], index) => (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3 text-center"
                  >
                    <div className={`w-8 h-8 mx-auto mb-2 rounded-sm flex items-center justify-center ${badge.color}`}>
                      {key === 'diamond' ? <Crown className="w-4 h-4" /> :
                       key === 'platinum' ? <Star className="w-4 h-4" /> :
                       key === 'gold' ? <Trophy className="w-4 h-4" /> :
                       <Medal className="w-4 h-4" />}
                    </div>
                    <p className="text-xs text-white font-medium">{badge.label}</p>
                    <p className="text-[10px] text-white/40 font-mono mt-1">≥ {formatINR(badge.threshold)}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* What to expect */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
              <div className="bg-white/5 border border-white/10 rounded-sm p-4">
                <Trophy className="w-5 h-5 text-[#4ADE80] mb-2" />
                <h4 className="text-sm text-white font-medium">Rankings</h4>
                <p className="text-xs text-white/50 mt-1">Compete with other brokers based on capital raised</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-sm p-4">
                <Medal className="w-5 h-5 text-yellow-400 mb-2" />
                <h4 className="text-sm text-white font-medium">Tier Badges</h4>
                <p className="text-xs text-white/50 mt-1">Earn Bronze to Diamond badges as you grow</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-sm p-4">
                <Star className="w-5 h-5 text-purple-400 mb-2" />
                <h4 className="text-sm text-white font-medium">Rewards</h4>
                <p className="text-xs text-white/50 mt-1">Unlock exclusive perks at higher tiers</p>
              </div>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
